// eslint-disable-next-line
import KeyValue from '@/models/KeyValue';
import EnvService from '@/services/EnvironmentService';

export class TrimChatbotService {
  constructor() {
    TrimChatbotService.polyfillAsyncIterable();
  }

  private static serviceInstance: TrimChatbotService;

  public static get instance() {
    // eslint-disable-next-line no-return-assign
    return this.serviceInstance || (this.serviceInstance = new this());
  }

  public async sendChatHistory(questionAnswers: KeyValue[],
    rangeId: number,
    correlationId: string,
    conversationId: string,
    botType: number): Promise<void> {
    const payload: any[] = [];

    questionAnswers.forEach((qa) => {
      const questionBody = {
        author: 3,
        correlationId,
        conversationId,
        content: qa.key,
        rangeId,
        timestamp: new Date().toISOString(),
        botType
      };
      const answerBody = {
        author: 2,
        correlationId,
        conversationId,
        content: qa.value,
        rangeId,
        timestamp: new Date().toISOString(),
        botType
      };
      payload.push(questionBody);
      payload.push(answerBody);
    });

    const response = await fetch(
      `${this.gatewayDomain}StoreTrimbotEvent`,
      {
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json'
        },
        method: 'POST'
      }
    );
  }

  public async sendComplaint(complaintType: string, message: string, correlationId: string): Promise<void> {
    if (complaintType == null || complaintType.length < 1) {
      return;
    }
    const body = {
      ComplaintType: parseInt(complaintType, 10),
      ComplaintMessage: message,
      correlationId
    };
    const response = await fetch(
      `${this.gatewayDomain}TrimbotComplaint
    `,
      {
        body: JSON.stringify(body),
        headers: {
          'Content-Type': 'application/json'
        },
        method: 'POST'
      }
    );
  }

  public async sendRating(rating: number, correlationId: string): Promise<void> {
    const body = {
      Rating: rating,
      correlationId
    };
    const response = await fetch(
      `${this.gatewayDomain}TrimbotRating
    `,
      {
        body: JSON.stringify(body),
        headers: {
          'Content-Type': 'application/json'
        },
        method: 'POST'
      }
    );
  }

  public async sendMessage(
    chatText: string,
    rangeId: number,
    correlationId: string,
    conversationId: string,
    isRetry: boolean,
    botType: number,
    onChunkReceived: (token: string) => void,
    retryCount: number = 0
  ): Promise<void> {
    const body = {
      question: chatText,
      rangeId,
      correlationId,
      conversationId,
      isRetry
    };

    try {
      const response = await fetch(
        `${this.gatewayDomain}Chat/${botType}`,
        {
          body: JSON.stringify(body),
          headers: {
            'Content-Type': 'application/json'
          },
          method: 'POST'
        }
      );

      if (response.ok && response.body) {
        const stream: any = response.body;
        const decoder = new TextDecoder();

        // eslint-disable-next-line
        for await (const chunk of stream) {
          // Do something with each 'chunk'
          const text = decoder.decode(chunk);
          onChunkReceived(text);
        }

        return;
      }

      // If we're not an OK response we should have a JSON body
      const content = await response.json();
      if (content.CanRetry && retryCount < 3) {
        await this.sendMessage(chatText, rangeId, correlationId, conversationId, isRetry, botType, onChunkReceived, retryCount + 1);
      }
      onChunkReceived(content.Response);
    } catch (error) {
      onChunkReceived('\n An error occurred while processing your request. Please try again later.');
    }
  }

  public async warmUp() {
    try {
      const response = await fetch(
        `${this.gatewayDomain}WarmUp`,
        {
          method: 'GET'
        }
      );
      return response.ok;
    } catch (error) {
      return false;
    }
  }

  private get gatewayDomain(): string {
    return `https://tla-func-trimbot-gateway-${EnvService.getEnv()}.azurewebsites.net/api/`;
  }

  // From https://github.com/DefinitelyTyped/DefinitelyTyped/discussions/65542#discussioncomment-6071004
  private static polyfillAsyncIterable() {
    if (!ReadableStream.prototype[Symbol.asyncIterator]) {
      console.log('need async iterable polyfill.');
      // eslint-disable-next-line
      ReadableStream.prototype[Symbol.asyncIterator] = async function* () {
        const reader = this.getReader();
        try {
          while (true) {
            // eslint-disable-next-line
            const { done, value } = await reader.read();
            if (done) return;
            yield value;
          }
        } finally {
          reader.releaseLock();
        }
      };
    }
  }
}

// export a singleton instance in the global namespace
// eslint-disable-next-line import/prefer-default-export
export const trimChatbotService = TrimChatbotService.instance;
